import React, { useState, useEffect } from "react";
import { Box, Typography, Button, IconButton, keyframes } from "@mui/material";
import { useTheme } from "@emotion/react";
import { UpgradeConsumerButton } from "./consumer/UpgradeConsumerButton.js";
import DownloadSampleButton from "../instructions/DownloadSampleButton.js";

// Floating animations for decorative elements
const float1 = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  25% { transform: translate(5px, -5px) rotate(1deg); }
  50% { transform: translate(-3px, 3px) rotate(-1deg); }
  75% { transform: translate(2px, -2px) rotate(0.5deg); }
`;

const float2 = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  33% { transform: translate(-4px, 6px) rotate(-1deg); }
  66% { transform: translate(6px, -4px) rotate(1deg); }
`;

const float3 = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  20% { transform: translate(3px, 3px) rotate(0.5deg); }
  40% { transform: translate(-2px, -3px) rotate(-0.5deg); }
  60% { transform: translate(4px, -2px) rotate(1deg); }
  80% { transform: translate(-3px, 2px) rotate(-0.5deg); }
`;

const BenefitsCarousel = ({
  variant = "light",
  showDownloadSample = true,
  onUpgradeClick,
  sx,
  ...props
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const theme = useTheme();
  const { primary, secondary, text, overlay } = theme.palette;
  const isDark = variant === "dark";

  const benefits = [
    {
      title: "Transparent Updates",
      description:
        "Always know when your calendar last refreshed and what changed.",
      icon: "visibility",
      background: primary.main,
    },
    {
      title: "Faster Updates",
      description:
        "Stay in sync with near real-time event updates, no more waiting 24-48 hours.",
      icon: "sync",
      background: primary.main,
    },
    {
      title: "Unlimited Calendars",
      description:
        "Subscribe to as many calendars as you want. No limits, ever.",
      icon: "calendar_month",
      background: primary.main,
    },
    {
      title: "Smart Organization",
      description:
        "Group calendars together and get personalized event recommendations.",
      icon: "auto_awesome",
      background: primary.main,
    },
  ];

  const handleDotClick = (index) => {
    setCurrentSlide(index);
  };

  const currentBenefit = benefits[currentSlide];

  // Dark mode styles
  const containerStyles = isDark
    ? {
        backgroundColor: primary.main,
        background: primary.main,
        border: `1px solid ${overlay.white30}`,
      }
    : {
        backgroundColor: theme.palette.background.paper,
      };

  const textColor = isDark ? primary.contrastText : text.primary;
  const secondaryTextColor = isDark ? overlay.white70 : text.secondary;

  return (
    <Box
      sx={{
        textAlign: "center",
        py: 4,
        px: 4,
        borderRadius: 8,
        ...containerStyles,
        ...sx,
      }}
      {...props}
    >
      {/* Title */}
      <Box
        sx={{
          fontSize: "3rem",
          color: textColor,
          textShadow: "0 2px 8px rgba(0,0,0,0.2)",
          fontFamily: "Material Symbols Outlined",
          fontWeight: 100,
          display: "block",
          margin: "0 auto",
        }}
      >
        rocket_launch
      </Box>
      <Typography
        variant="title"
        sx={{
          color: textColor,
          lineHeight: 1.2,
          fontWeight: 300,
        }}
      >
        Go Plus.{" "}
        <Box component="span" sx={{ fontWeight: 600 }}>
          Go Big.
        </Box>
      </Typography>

      {/* Visual Card */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "180px",
          margin: "0 auto 2rem",
          borderRadius: "16px",
          backgroundColor: currentBenefit.background,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          boxShadow: theme.shadows.cardSm(
            theme.palette,
            isDark ? "dark" : "light"
          ),
          transition: "all 0.6s ease-in-out",
          mt: 4,
        }}
      >
        {/* Decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: "20px",
            left: "20px",
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: overlay.white30,
            animation: `${float1} 6s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "40px",
            right: "30px",
            width: "12px",
            height: "12px",
            borderRadius: "50%",
            backgroundColor: overlay.white20,
            animation: `${float2} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "30px",
            left: "30px",
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: overlay.white40,
            animation: `${float3} 7s ease-in-out infinite`,
          }}
        />

        {/* Additional dark mode sparkles */}
        {isDark && (
          <>
            <Box
              sx={{
                position: "absolute",
                top: "60px",
                right: "60px",
                width: "4px",
                height: "4px",
                borderRadius: "50%",
                backgroundColor: overlay.white60,
                animation: `${float2} 5s ease-in-out infinite reverse`,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: "60px",
                right: "40px",
                width: "3px",
                height: "3px",
                borderRadius: "50%",
                backgroundColor: overlay.white50,
                animation: `${float1} 9s ease-in-out infinite`,
              }}
            />
          </>
        )}

        {/* Main Icon */}
        <Box
          sx={{
            fontSize: "4rem",
            color: primary.contrastText,
            textShadow: "0 2px 8px rgba(0,0,0,0.2)",
            fontFamily: "Material Symbols Outlined",
            fontWeight: 300,
          }}
        >
          {currentBenefit.icon}
        </Box>
      </Box>

      {/* Content */}
      <Typography
        variant="h4"
        sx={{
          mb: 1.5,
          fontWeight: 600,
          color: textColor,
          transition: "all 0.3s ease",
        }}
      >
        {currentBenefit.title}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          mb: 3,
          color: secondaryTextColor,
          maxWidth: "300px",
          margin: "0 auto 1.5rem",
          lineHeight: 1.5,
          transition: "all 0.3s ease",
        }}
      >
        {currentBenefit.description}
      </Typography>

      {/* Pagination Dots */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 3 }}>
        {benefits.map((_, index) => (
          <Box
            key={index}
            onClick={() => handleDotClick(index)}
            sx={{
              width: index === currentSlide ? "24px" : "8px",
              height: "8px",
              borderRadius: "4px",
              backgroundColor:
                index === currentSlide
                  ? isDark
                    ? primary.contrastText
                    : primary.main
                  : isDark
                  ? overlay.white30
                  : theme.palette.action.disabled,
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: isDark ? primary.contrastText : primary.main,
                opacity: 0.8,
              },
            }}
          />
        ))}
      </Box>

      {/* Download Sample Button */}
      {showDownloadSample && (
        <DownloadSampleButton
          variant="outlined"
          sx={{
            mb: 2,
            borderRadius: "25px",
            fontWeight: 600,
            textTransform: "none",
            ...(isDark && {
              borderColor: overlay.white30,
              color: primary.contrastText,
              "&:hover": {
                borderColor: primary.contrastText,
                backgroundColor: overlay.white10,
              },
            }),
          }}
        />
      )}

      {/* CTA Button */}
      <UpgradeConsumerButton
        sx={{
          fontWeight: 600,
          textTransform: "none",
          boxShadow: theme.shadows.cardSm(
            theme.palette,
            isDark ? "dark" : "light"
          ),
          ...(isDark && {
            backgroundColor: secondary.main,
            color: secondary.contrastText,
            "&:hover": {
              backgroundColor: overlay.white90,
            },
          }),
        }}
        onClick={onUpgradeClick}
      >
        START YOUR TRIAL
      </UpgradeConsumerButton>
    </Box>
  );
};

export default BenefitsCarousel;
